import { actionCreatorFactory } from 'typescript-fsa';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { asyncFactory } from 'typescript-fsa-redux-thunk';

export function defineFetchForNamespace<Data, Params, NS extends string, Name extends string>(
    namespace: NS,
    name: Name,
    fetch: (params: Params) => Promise<Data>,
) {
    type State = {
        isLoading: boolean;
        isInitial: boolean;
        data?: Data;
    };

    type GlobalState = { [key in NS]: { [key in Name]: State } };

    const initialState: State = {
        isLoading: false,
        isInitial: true,
    };

    const getData = (state: GlobalState) => state[namespace][name].data;
    const getIsLoading = (state: GlobalState) => state[namespace][name].isLoading;
    const getIsInitial = (state: GlobalState) => state[namespace][name].isInitial;

    const createAction = actionCreatorFactory(namespace);
    const createAsyncAction = asyncFactory<GlobalState>(createAction);

    const fetchData = createAsyncAction<Params, Data>('FETCH', params => {
        return fetch(params);
    });

    const resetData = createAction('RESET');

    const reducer = reducerWithInitialState<State>(initialState)
        .case(fetchData.async.started, state => ({
            ...state,
            isLoading: true,
            isInitial: false,
        }))
        .case(fetchData.async.failed, state => ({
            ...state,
            isLoading: false,
        }))
        .case(fetchData.async.done, (state, { result }) => ({
            ...state,
            isLoading: false,
            data: result,
        }))
        .case(resetData, state => ({
            ...state,
            data: undefined,
        }));

    return {
        reducer,
        fetchData: fetchData.action,
        getData,
        getIsInitial,
        getIsLoading,
        resetData,
        getInitialState: (): State => initialState,
    };
}
